/**
 * Video thumbnail extraction utilities
 * Creates thumbnails from video files using HTML5 video element
 */

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_THUMBNAIL_TIMESTAMP = 1; // 1 second into video
const THUMBNAIL_QUALITY = 0.8; // JPEG quality
const THUMBNAIL_FORMAT = 'image/jpeg';

// ============================================================================
// THUMBNAIL EXTRACTION
// ============================================================================

/**
 * Extract a thumbnail from a video file at a specific timestamp
 * @param videoFile - The video file to extract from
 * @param timestamp - Timestamp in seconds (default: 1 second)
 * @returns Thumbnail as a Blob
 */
export async function extractThumbnail(
  videoFile: File,
  timestamp: number = DEFAULT_THUMBNAIL_TIMESTAMP
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Failed to get canvas 2D context'));
      return;
    }

    video.preload = 'metadata';
    video.muted = true;

    // Handle video loaded metadata
    video.addEventListener('loadedmetadata', () => {
      // Ensure timestamp is within video duration
      const seekTime = Math.min(timestamp, video.duration - 0.1);
      video.currentTime = seekTime;
    });

    // Handle seek completion
    video.addEventListener('seeked', () => {
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create thumbnail blob'));
          }

          // Clean up
          video.src = '';
          URL.revokeObjectURL(video.src);
        },
        THUMBNAIL_FORMAT,
        THUMBNAIL_QUALITY
      );
    });

    // Handle errors
    video.addEventListener('error', () => {
      reject(
        new Error(
          `Failed to load video: ${video.error?.message || 'Unknown error'}`
        )
      );
      URL.revokeObjectURL(video.src);
    });

    // Load video
    video.src = URL.createObjectURL(videoFile);
  });
}

/**
 * Extract a thumbnail from a video file and return as data URL
 * @param videoFile - The video file to extract from
 * @param timestamp - Timestamp in seconds (default: 1 second)
 * @returns Thumbnail as a data URL string
 */
export async function getThumbnailDataUrl(
  videoFile: File,
  timestamp: number = DEFAULT_THUMBNAIL_TIMESTAMP
): Promise<string> {
  const blob = await extractThumbnail(videoFile, timestamp);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read thumbnail as data URL'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read thumbnail blob'));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Get the duration of a video file in seconds
 * @param videoFile - The video file to analyze
 * @returns Duration in seconds
 */
export async function getVideoDuration(videoFile: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    video.addEventListener('loadedmetadata', () => {
      const duration = video.duration;

      // Clean up
      video.src = '';
      URL.revokeObjectURL(video.src);

      if (isFinite(duration) && duration > 0) {
        resolve(duration);
      } else {
        reject(new Error('Invalid video duration'));
      }
    });

    video.addEventListener('error', () => {
      reject(
        new Error(
          `Failed to load video: ${video.error?.message || 'Unknown error'}`
        )
      );
      URL.revokeObjectURL(video.src);
    });

    video.src = URL.createObjectURL(videoFile);
  });
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format duration in seconds to HH:MM:SS or MM:SS
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Check if a file is a video
 * @param file - File to check
 * @returns True if file is a video
 */
export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

/**
 * Check if a file is an image
 * @param file - File to check
 * @returns True if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}
